import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Space, Tabs, Statistic, Progress, Tooltip, Spin, Alert, Divider, Input, Modal, Radio } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, DeleteOutlined, ClockCircleOutlined, SearchOutlined, ToolOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTranslationWithNamespace } from '../hooks/useTranslationWithNamespace';
import { showSuccessNotification, showErrorNotification, showWarningNotification } from '../utils/notificationUtils';
import { ensureNamespacesLoaded } from '../utils/i18nUtils';
import {
  useQueueMetrics,
  useDepositQueueStats,
  useWithdrawalQueueStats,
  useWebhookQueueStats,
  useSweepQueueStats,
  useSweepHistory,
  usePauseQueue,
  useResumeQueue,
  useProcessQueue,
  useClearFailedJobs
} from '../hooks/useQueueManagement';
import { useResetStuckWithdrawalTransactions } from '../hooks/useWithdrawalReset';
import { useResetStuckDepositTransactions } from '../hooks/useDepositReset';
import QueueJobsModal from '../components/queue/QueueJobsModal';
import JobSearchModal from '../components/queue/JobSearchModal';
import CircuitBreakerPanel from '../components/queue/CircuitBreakerPanel';
import FailedTransactionsPanel from '../components/queue/FailedTransactionsPanel';
import ManualInterventionModal from '../components/queue/ManualInterventionModal';
import { JobType } from '../api/queues';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const QueueManagementPage: React.FC = () => {
  const { t: commonT } = useTranslation();
  const { t: queueT } = useTranslationWithNamespace('queue');
  const [activeTab, setActiveTab] = useState('deposit');

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState('');
  const [selectedJobType, setSelectedJobType] = useState<JobType>(JobType.WAITING);

  // Search modal state
  const [searchModalVisible, setSearchModalVisible] = useState(false);

  // Reset stuck transactions modal state
  const [resetStuckModalVisible, setResetStuckModalVisible] = useState(false);
  const [resetMinutes, setResetMinutes] = useState(10);
  const [transactionType, setTransactionType] = useState<'withdrawal' | 'deposit'>('withdrawal');

  // Manual intervention modal state
  const [manualInterventionModalVisible, setManualInterventionModalVisible] = useState(false);

  // Reset stuck transactions mutations
  const resetStuckWithdrawalTransactionsMutation = useResetStuckWithdrawalTransactions();
  const resetStuckDepositTransactionsMutation = useResetStuckDepositTransactions();

  // Ensure the necessary namespaces are loaded
  useEffect(() => {
    ensureNamespacesLoaded(['descriptions.deposit', 'descriptions.withdrawal']);
  }, []);

  // Fetch queue data from API
  const { data: queueMetrics, isLoading: isLoadingMetrics, isError: isMetricsError } = useQueueMetrics();
  const { data: depositStats, isLoading: isLoadingDeposit, isError: isDepositError } = useDepositQueueStats();
  const { data: withdrawalStats, isLoading: isLoadingWithdrawal, isError: isWithdrawalError } = useWithdrawalQueueStats();
  const { data: webhookStats, isLoading: isLoadingWebhook, isError: isWebhookError } = useWebhookQueueStats();
  const { data: sweepStats, isLoading: isLoadingSweep, isError: isSweepError } = useSweepQueueStats();
  const { data: sweepHistory, isLoading: isLoadingSweepHistory } = useSweepHistory();

  // Queue management mutations
  const pauseQueueMutation = usePauseQueue();
  const resumeQueueMutation = useResumeQueue();
  const processQueueMutation = useProcessQueue();
  const clearFailedJobsMutation = useClearFailedJobs();

  // Loading state
  const isLoading = isLoadingMetrics || isLoadingDeposit || isLoadingWithdrawal || isLoadingWebhook || isLoadingSweep;

  // Error state
  const hasError = isMetricsError || isDepositError || isWithdrawalError || isWebhookError || isSweepError;

  // Format deposit queues from API data
  const depositQueues = depositStats?.queues ?
    depositStats.queues.map((queue: any) => ({
      key: queue.key || queue.name,
      name: queue.name,
      description: getQueueDescription(queue.name),
      waiting: queue.waiting || 0,
      active: queue.active || 0,
      completed: queue.completed || 0,
      failed: queue.failed || 0,
      delayed: queue.delayed || 0,
      paused: queue.paused || false
    })) : [];

  // Format withdrawal queues from API data
  const withdrawalQueues = withdrawalStats?.queues ?
    withdrawalStats.queues.map((queue: any) => ({
      key: queue.key || queue.name,
      name: queue.name,
      description: getQueueDescription(queue.name),
      waiting: queue.waiting || 0,
      active: queue.active || 0,
      completed: queue.completed || 0,
      failed: queue.failed || 0,
      delayed: queue.delayed || 0,
      paused: queue.paused || false
    })) : [];

  // Format sweep queues from API data
  const sweepQueues = sweepStats?.queues ?
    sweepStats.queues.map((queue: any) => ({
      key: queue.key || queue.name,
      name: queue.name,
      description: getQueueDescription(queue.name),
      waiting: queue.waiting || 0,
      active: queue.active || 0,
      completed: queue.completed || 0,
      failed: queue.failed || 0,
      delayed: queue.delayed || 0,
      paused: queue.paused || false
    })) : [];

  // Format other queues from API data (webhook, balance, transaction, etc.)
  const otherQueues = [
    // Webhook queues
    ...(webhookStats?.queues ?
      webhookStats.queues.map((queue: any) => ({
        key: queue.key || queue.name,
        name: queue.name,
        description: getQueueDescription(queue.name),
        waiting: queue.waiting || 0,
        active: queue.active || 0,
        completed: queue.completed || 0,
        failed: queue.failed || 0,
        delayed: queue.delayed || 0,
        paused: queue.paused || false
      })) : []),

    // Balance queue
    {
      key: 'balanceQueue',
      name: 'balanceQueue',
      description: 'Queue for wallet balance operations',
      waiting: queueMetrics?.balanceQueue?.waiting || 0,
      active: queueMetrics?.balanceQueue?.active || 0,
      completed: queueMetrics?.balanceQueue?.completed || 0,
      failed: queueMetrics?.balanceQueue?.failed || 0,
      delayed: queueMetrics?.balanceQueue?.delayed || 0,
      paused: false
    },

    // Transaction queue
    {
      key: 'transactionQueue',
      name: 'transactionQueue',
      description: 'Queue for transaction operations',
      waiting: queueMetrics?.transactionQueue?.waiting || 0,
      active: queueMetrics?.transactionQueue?.active || 0,
      completed: queueMetrics?.transactionQueue?.completed || 0,
      failed: queueMetrics?.transactionQueue?.failed || 0,
      delayed: queueMetrics?.transactionQueue?.delayed || 0,
      paused: false
    },

    // Callback queue
    {
      key: 'callbackQueue',
      name: 'callbackQueue',
      description: 'Queue for general callbacks',
      waiting: queueMetrics?.callbackQueue?.waiting || 0,
      active: queueMetrics?.callbackQueue?.active || 0,
      completed: queueMetrics?.callbackQueue?.completed || 0,
      failed: queueMetrics?.callbackQueue?.failed || 0,
      delayed: queueMetrics?.callbackQueue?.delayed || 0,
      paused: false
    }
  ];

  // Helper function to get queue description
  function getQueueDescription(queueName: string): string {
    // Try to get the description from translations
    const translationKey = `descriptions.${queueName}`;
    const description = queueT(translationKey);

    // If the translation exists and is not the same as the key, return it
    if (description !== translationKey) {
      return description;
    }

    // Fallback descriptions if translation is not available
    const fallbackDescriptions: Record<string, string> = {
      'deposit:received': 'Queue for newly received deposits',
      'deposit:topup': 'Queue for deposits requiring hot wallet top-up',
      'deposit:processing': 'Queue for deposits being processed',
      'deposit:callback': 'Queue for deposit callbacks',
      'deposit:webhook': 'Queue for deposit webhook notifications',
      'deposit:deadletter': 'Dead letter queue for failed deposits',
      'withdrawal:requested': 'Queue for requested withdrawals',
      'withdrawal:approved': 'Queue for approved withdrawals',
      'withdrawal:broadcast': 'Queue for broadcast withdrawals',
      'withdrawal:confirmed': 'Queue for confirmed withdrawals',
      'withdrawal:failed': 'Queue for failed withdrawals',
      'withdrawal:deadletter': 'Dead letter queue for failed withdrawals',
      'webhook:deposit': 'Queue for deposit webhook notifications',
      'webhook:withdrawal': 'Queue for withdrawal webhook notifications',
      'webhook:deadletter': 'Dead letter queue for failed webhook notifications',
      'balanceQueue': 'Queue for wallet balance operations',
      'transactionQueue': 'Queue for transaction operations',
      'callbackQueue': 'Queue for general callbacks (including withdrawal callbacks)'
    };

    return fallbackDescriptions[queueName] || `Queue for ${queueName} operations`;
  }

  // Helper function to format queue name for display
  function formatQueueName(queueName: string): string {
    // If the queue name contains a colon (e.g., 'withdrawal:requested')
    if (queueName.includes(':')) {
      const [type, action] = queueName.split(':');

      // Capitalize the first letter of each part
      const formattedType = type.charAt(0).toUpperCase() + type.slice(1);
      const formattedAction = action.charAt(0).toUpperCase() + action.slice(1);

      return `${formattedType} ${formattedAction}`;
    }

    // For other queue names, capitalize and add spaces before capital letters
    if (queueName) {
      // First capitalize the first letter
      let formatted = queueName.charAt(0).toUpperCase() + queueName.slice(1);

      // Add spaces before capital letters in camelCase
      formatted = formatted.replace(/([A-Z])/g, ' $1').trim();

      return formatted;
    }

    // Fallback
    return queueName;
  }



  // Table columns
  const columns = [
    {
      title: queueT('name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: queueT('description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: queueT('waiting'),
      dataIndex: 'waiting',
      key: 'waiting',
      render: (waiting: number, record: any) => (
        <Button
          type="link"
          onClick={() => openJobsModal(record.name, JobType.WAITING)}
          style={{ padding: 0 }}
          disabled={waiting === 0}
        >
          <Statistic
            value={waiting}
            valueStyle={{ fontSize: '14px', color: waiting > 0 ? '#1890ff' : 'inherit' }}
          />
        </Button>
      )
    },
    {
      title: queueT('active'),
      dataIndex: 'active',
      key: 'active',
      render: (active: number, record: any) => (
        <Button
          type="link"
          onClick={() => openJobsModal(record.name, JobType.ACTIVE)}
          style={{ padding: 0 }}
          disabled={active === 0}
        >
          <Statistic
            value={active}
            valueStyle={{ fontSize: '14px', color: active > 0 ? '#52c41a' : 'inherit' }}
          />
        </Button>
      )
    },
    {
      title: queueT('completed'),
      dataIndex: 'completed',
      key: 'completed',
      render: (completed: number, record: any) => (
        <Button
          type="link"
          onClick={() => openJobsModal(record.name, JobType.COMPLETED)}
          style={{ padding: 0 }}
          disabled={completed === 0}
        >
          <Statistic
            value={completed}
            valueStyle={{ fontSize: '14px' }}
          />
        </Button>
      )
    },
    {
      title: queueT('failed'),
      dataIndex: 'failed',
      key: 'failed',
      render: (failed: number, record: any) => (
        <Button
          type="link"
          onClick={() => openJobsModal(record.name, JobType.FAILED)}
          style={{ padding: 0 }}
          disabled={failed === 0}
        >
          <Statistic
            value={failed}
            valueStyle={{ fontSize: '14px', color: failed > 0 ? '#f5222d' : 'inherit' }}
          />
        </Button>
      )
    },
    {
      title: queueT('status'),
      dataIndex: 'paused',
      key: 'status',
      render: (paused: boolean) => (
        paused ?
          <Tag color="orange" icon={<PauseCircleOutlined />}>{queueT('paused')}</Tag> :
          <Tag color="green" icon={<PlayCircleOutlined />}>{queueT('active')}</Tag>
      )
    },
    {
      title: queueT('health'),
      key: 'health',
      render: (_: any, record: any) => {
        const total = record.completed + record.failed;
        const successRate = total > 0 ? (record.completed / total) * 100 : 100;

        let color = 'success';
        if (successRate < 90) color = 'warning';
        if (successRate < 80) color = 'exception';

        return (
          <Tooltip title={`${successRate.toFixed(1)}% ${queueT('metrics.successRate')}`}>
            <Progress
              percent={Math.round(successRate)}
              size="small"
              status={color as any}
              showInfo={false}
              style={{ width: 80 }}
            />
          </Tooltip>
        );
      }
    },
    {
      title: commonT('actions.title'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          {record.paused ? (
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleResume(record)}
              type="primary"
            >
              {queueT('resume')}
            </Button>
          ) : (
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              onClick={() => handlePause(record)}
            >
              {queueT('pause')}
            </Button>
          )}
          {record.waiting > 0 && (
            <Button
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => handleProcess(record)}
            >
              {queueT('process')}
            </Button>
          )}
          {record.failed > 0 && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleClearFailed(record)}
            >
              {queueT('clearFailed')}
            </Button>
          )}
        </Space>
      )
    }
  ];

  // Handle pausing a queue
  const handlePause = (queue: any) => {
    console.log('Pausing queue:', queue.name);
    pauseQueueMutation.mutate(queue.name, {
      onSuccess: () => {
        const title = queueT('notifications.queuePaused', 'Queue paused');
        const formattedQueueName = formatQueueName(queue.name);
        const message = queueT('notifications.queuePaused', 'Queue {{name}} has been paused', {
          name: formattedQueueName,
          interpolation: { escapeValue: false }
        });
        showSuccessNotification(title, message);
      },
      onError: (error: any) => {
        showErrorNotification(
          queueT('error.pauseQueue', 'Failed to pause queue'),
          error?.message || queueT('error.unknown', 'An unknown error occurred')
        );
      }
    });
  };

  // Handle resuming a queue
  const handleResume = (queue: any) => {
    console.log('Resuming queue:', queue.name);
    resumeQueueMutation.mutate(queue.name, {
      onSuccess: () => {
        const title = queueT('notifications.queueResumed', 'Queue resumed');
        const formattedQueueName = formatQueueName(queue.name);
        const message = queueT('notifications.queueResumed', 'Queue {{name}} has been resumed', {
          name: formattedQueueName,
          interpolation: { escapeValue: false }
        });
        showSuccessNotification(title, message);
      },
      onError: (error: any) => {
        showErrorNotification(
          queueT('error.resumeQueue', 'Failed to resume queue'),
          error?.message || queueT('error.unknown', 'An unknown error occurred')
        );
      }
    });
  };

  // Handle processing a queue
  const handleProcess = (queue: any) => {
    console.log('Processing queue:', queue.name);
    processQueueMutation.mutate(queue.name, {
      onSuccess: () => {
        const title = queueT('notifications.queueProcessed', 'Queue processing');
        const formattedQueueName = formatQueueName(queue.name);
        const message = queueT('notifications.queueProcessed', 'Queue {{name}} is being processed', {
          name: formattedQueueName,
          interpolation: { escapeValue: false }
        });
        showSuccessNotification(title, message);
      },
      onError: (error: any) => {
        showErrorNotification(
          queueT('error.processQueue', 'Failed to process queue'),
          error?.message || queueT('error.unknown', 'An unknown error occurred')
        );
      }
    });
  };

  // Handle clearing failed jobs
  const handleClearFailed = (queue: any) => {
    console.log('Clearing failed jobs for queue:', queue.name);
    clearFailedJobsMutation.mutate(queue.name, {
      onSuccess: () => {
        const title = queueT('notifications.failedJobsCleared', 'Failed jobs cleared');
        const formattedQueueName = formatQueueName(queue.name);
        const message = queueT('notifications.failedJobsCleared', 'Failed jobs in queue {{name}} have been cleared', {
          name: formattedQueueName,
          interpolation: { escapeValue: false }
        });
        showSuccessNotification(title, message);
      },
      onError: (error: any) => {
        showErrorNotification(
          queueT('error.clearFailedJobs', 'Failed to clear failed jobs'),
          error?.message || queueT('error.unknown', 'An unknown error occurred')
        );
      }
    });
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Open modal with job details
  const openJobsModal = (queueName: string, jobType: JobType) => {
    setSelectedQueue(queueName);
    setSelectedJobType(jobType);
    setModalVisible(true);
  };

  // Close modal
  const closeJobsModal = () => {
    setModalVisible(false);
  };

  // Open search modal
  const openSearchModal = () => {
    setSearchModalVisible(true);
  };

  // Close search modal
  const closeSearchModal = () => {
    setSearchModalVisible(false);
  };

  // Open reset stuck transactions modal
  const openResetStuckModal = () => {
    setResetStuckModalVisible(true);
  };

  // Close reset stuck transactions modal
  const closeResetStuckModal = () => {
    setResetStuckModalVisible(false);
  };

  // Open manual intervention modal
  const openManualInterventionModal = () => {
    setManualInterventionModalVisible(true);
  };

  // Close manual intervention modal
  const closeManualInterventionModal = () => {
    setManualInterventionModalVisible(false);
  };

  // Reset stuck transactions
  const resetStuckTransactions = () => {
    // Define statuses based on transaction type
    const withdrawalStatuses = [
      'Requested', 'Approved', 'Broadcast', 'Confirmed', 'Failed', 'Pending', 'Completed'
    ];

    const depositStatuses = [
      'Detected', 'Confirmed', 'Processed', 'Failed', 'Pending'
    ];

    const options = {
      olderThanMinutes: resetMinutes,
      statuses: transactionType === 'withdrawal' ? withdrawalStatuses : depositStatuses,
      maxResetAttempts: 5, // Maximum number of reset attempts before requiring manual intervention
      resetAttemptsThreshold: 3, // Show warning when reset attempts exceed this threshold
      markForManualIntervention: true // Mark transactions for manual intervention when max attempts reached
    };

    // Use the appropriate mutation based on transaction type
    if (transactionType === 'withdrawal') {
      resetStuckWithdrawalTransactionsMutation.mutate(options, {
        onSuccess: (data) => {
          closeResetStuckModal();

          // Show additional information about transactions that require manual intervention
          if (data.manualInterventionCount && data.manualInterventionCount > 0) {
            showWarningNotification(
              queueT('resetStuck.manualInterventionRequired', 'Manual Intervention Required'),
              queueT(
                'resetStuck.manualInterventionRequiredMessage',
                '{{count}} transactions have reached maximum reset attempts and require manual intervention',
                { count: data.manualInterventionCount }
              )
            );
          }

          // Refetch all queue data
          const queryClient = window.queryClient;
          if (queryClient) {
            queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
            queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
          }
        }
      });
    } else {
      resetStuckDepositTransactionsMutation.mutate(options, {
        onSuccess: (data) => {
          closeResetStuckModal();

          // Show additional information about transactions that require manual intervention
          if (data.manualInterventionCount && data.manualInterventionCount > 0) {
            showWarningNotification(
              queueT('resetStuck.manualInterventionRequired', 'Manual Intervention Required'),
              queueT(
                'resetStuck.manualInterventionRequiredMessage',
                '{{count}} transactions have reached maximum reset attempts and require manual intervention',
                { count: data.manualInterventionCount }
              )
            );
          }

          // Refetch all queue data
          const queryClient = window.queryClient;
          if (queryClient) {
            queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
            queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
          }
        }
      });
    }
  };

  // Handle job found from search
  const handleJobFound = (_job: any, queueName: string, jobType: string) => {
    // Convert job type string to JobType enum
    let jobTypeEnum: JobType;
    switch (jobType) {
      case 'waiting':
        jobTypeEnum = JobType.WAITING;
        break;
      case 'active':
        jobTypeEnum = JobType.ACTIVE;
        break;
      case 'completed':
        jobTypeEnum = JobType.COMPLETED;
        break;
      case 'failed':
        jobTypeEnum = JobType.FAILED;
        break;
      case 'delayed':
        jobTypeEnum = JobType.DELAYED;
        break;
      default:
        jobTypeEnum = JobType.WAITING;
    }

    // Set the selected queue and job type
    setSelectedQueue(queueName);
    setSelectedJobType(jobTypeEnum);

    // Open the jobs modal
    setModalVisible(true);
  };

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col flex="auto">
          <Title level={2}>{queueT('title')}</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<SearchOutlined />}
              onClick={openSearchModal}
              type="primary"
            >
              {queueT('searchJob.button', 'Search Job')}
            </Button>
            <Button
              icon={<MedicineBoxOutlined />}
              onClick={openManualInterventionModal}
              type="primary"
              danger
            >
              {queueT('manualIntervention.button', 'Manual Intervention')}
            </Button>
            <Button
              icon={<ToolOutlined />}
              onClick={openResetStuckModal}
              type="default"
            >
              {queueT('resetStuck.button', 'Reset Stuck Transactions')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                // Refetch all queue data
                const queryClient = window.queryClient;
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: ['queueMetrics'] });
                  queryClient.invalidateQueries({ queryKey: ['depositQueueStats'] });
                  queryClient.invalidateQueries({ queryKey: ['withdrawalQueueStats'] });
                  queryClient.invalidateQueries({ queryKey: ['webhookQueueStats'] });
                }
              }}
              loading={isLoading}
            >
              {commonT('actions.refresh')}
            </Button>
          </Space>
        </Col>
      </Row>

      {hasError && (
        <Alert
          message={queueT('errorTitle')}
          description={queueT('errorDescription')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab={queueT('depositQueues')} key="deposit">
            {isLoadingDeposit ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={depositQueues}
                pagination={false}
                rowKey="key"
                locale={{ emptyText: queueT('noQueues') }}
              />
            )}
          </TabPane>
          <TabPane tab={queueT('withdrawalQueues')} key="withdrawal">
            {isLoadingWithdrawal ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={withdrawalQueues}
                pagination={false}
                rowKey="key"
                locale={{ emptyText: queueT('noQueues') }}
              />
            )}
          </TabPane>
          <TabPane tab="Sweep Queues" key="sweep">
            {isLoadingSweep ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {/* Sweep Historical Statistics Card */}
                <Card
                  title="Sweep Historical Statistics"
                  style={{ marginBottom: 16 }}
                  loading={isLoadingSweepHistory}
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic
                        title="Total Swept"
                        value={sweepHistory?.stats?.total_swept || 0}
                        prefix={<ClockCircleOutlined />}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Completed"
                        value={sweepHistory?.stats?.completed || 0}
                        valueStyle={{ color: '#52c41a' }}
                        suffix={`/ ${sweepHistory?.stats?.total_swept || 0}`}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Not Needed"
                        value={sweepHistory?.stats?.not_needed || 0}
                        valueStyle={{ color: '#8c8c8c' }}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Success Rate"
                        value={sweepHistory?.stats?.success_rate || 0}
                        suffix="%"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Last 24 Hours"
                        value={sweepHistory?.stats?.last_24_hours || 0}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Last 7 Days"
                        value={sweepHistory?.stats?.last_7_days || 0}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Failed"
                        value={sweepHistory?.stats?.failed || 0}
                        valueStyle={{ color: sweepHistory?.stats?.failed > 0 ? '#ff4d4f' : '#8c8c8c' }}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Queue Stats Table */}
                <Table
                  columns={columns}
                  dataSource={sweepQueues}
                  pagination={false}
                  rowKey="key"
                  locale={{ emptyText: queueT('noQueues') }}
                />
              </>
            )}
          </TabPane>
          <TabPane tab={queueT('otherQueues')} key="other">
            {isLoadingMetrics || isLoadingWebhook ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={otherQueues}
                pagination={false}
                rowKey="key"
                locale={{ emptyText: queueT('noQueues') }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title={queueT('totalQueues')}
              value={depositQueues.length + withdrawalQueues.length + sweepQueues.length + otherQueues.length}
              prefix={<ClockCircleOutlined />}
              loading={isLoading}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={queueT('totalWaiting')}
              value={[...depositQueues, ...withdrawalQueues, ...sweepQueues, ...otherQueues].reduce((sum, q) => sum + q.waiting, 0)}
              valueStyle={{ color: '#1890ff' }}
              loading={isLoading}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={queueT('totalActive')}
              value={[...depositQueues, ...withdrawalQueues, ...sweepQueues, ...otherQueues].reduce((sum, q) => sum + q.active, 0)}
              valueStyle={{ color: '#52c41a' }}
              loading={isLoading}
            />
          </Col>
        </Row>
      </Card>

      {/* Circuit Breaker Panel */}
      <Divider />
      <CircuitBreakerPanel refreshInterval={0} />

      {/* Failed Transactions Panel */}
      <Divider />
      <FailedTransactionsPanel />

      {/* Queue Jobs Modal */}
      <QueueJobsModal
        visible={modalVisible}
        onClose={closeJobsModal}
        queueName={selectedQueue}
        jobType={selectedJobType}
      />

      {/* Job Search Modal */}
      <JobSearchModal
        visible={searchModalVisible}
        onClose={closeSearchModal}
        onJobFound={handleJobFound}
      />

      {/* Manual Intervention Modal */}
      <ManualInterventionModal
        visible={manualInterventionModalVisible}
        onClose={closeManualInterventionModal}
      />

      {/* Reset Stuck Transactions Modal */}
      <Modal
        title={queueT('resetStuck.title', 'Reset Stuck Transactions')}
        open={resetStuckModalVisible}
        onCancel={closeResetStuckModal}
        footer={[
          <Button key="cancel" onClick={closeResetStuckModal}>
            {commonT('actions.cancel')}
          </Button>,
          <Button
            key="reset"
            type="primary"
            onClick={resetStuckTransactions}
            loading={
              transactionType === 'withdrawal'
                ? resetStuckWithdrawalTransactionsMutation.isPending
                : resetStuckDepositTransactionsMutation.isPending
            }
          >
            {queueT('resetStuck.confirm', 'Reset Transactions')}
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message={queueT('resetStuck.warning', 'Warning')}
            description={queueT('resetStuck.warningDescription', 'This will reset all stuck transactions that have been in any state for too long. This operation cannot be undone.')}
            type="warning"
            showIcon
          />

          <div style={{ marginTop: 16 }}>
            <Text>{queueT('resetStuck.transactionType', 'Transaction Type:')}</Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value as 'withdrawal' | 'deposit')}
                buttonStyle="solid"
              >
                <Radio.Button value="withdrawal">{queueT('resetStuck.withdrawal', 'Withdrawal')}</Radio.Button>
                <Radio.Button value="deposit">{queueT('resetStuck.deposit', 'Deposit')}</Radio.Button>
              </Radio.Group>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Text>{queueT('resetStuck.olderThan', 'Reset transactions older than:')}</Text>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
              <Input
                type="number"
                value={resetMinutes}
                onChange={(e) => setResetMinutes(parseInt(e.target.value) || 10)}
                style={{ width: 100 }}
                min={1}
              />
              <Text style={{ marginLeft: 8 }}>{queueT('resetStuck.minutes', 'minutes')}</Text>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Text>{queueT('resetStuck.affectedStatuses', 'Affected transaction statuses:')}</Text>
            <div style={{ marginTop: 8 }}>
              {transactionType === 'withdrawal' ? (
                <Space wrap>
                  <Tag color="blue">Requested</Tag>
                  <Tag color="blue">Approved</Tag>
                  <Tag color="blue">Broadcast</Tag>
                  <Tag color="blue">Confirmed</Tag>
                  <Tag color="blue">Failed</Tag>
                  <Tag color="blue">Pending</Tag>
                  <Tag color="blue">Completed</Tag>
                </Space>
              ) : (
                <Space wrap>
                  <Tag color="green">Detected</Tag>
                  <Tag color="green">Confirmed</Tag>
                  <Tag color="green">Processed</Tag>
                  <Tag color="green">Failed</Tag>
                  <Tag color="green">Pending</Tag>
                </Space>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">{queueT('resetStuck.statusNote', 'Note: All transaction statuses will be considered for reset.')}</Text>
            </div>
          </div>

          <Alert
            message={queueT('resetStuck.resetAttemptsInfo', 'Reset Attempts Information')}
            description={
              <>
                <p>{queueT('resetStuck.resetAttemptsDescription', 'Transactions have a limit on how many times they can be automatically reset. After reaching the maximum number of reset attempts, they will be marked for manual intervention.')}</p>
                <ul>
                  <li>{queueT('resetStuck.maxResetAttempts', 'Maximum reset attempts: 5')}</li>
                  <li>{queueT('resetStuck.resetAttemptsWarningThreshold', 'Warning threshold: 3')}</li>
                </ul>
                <p>{queueT('resetStuck.manualInterventionNote', 'Transactions that require manual intervention will be visible in the Manual Intervention panel.')}</p>
              </>
            }
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />

          {(transactionType === 'withdrawal' && resetStuckWithdrawalTransactionsMutation.isError) && (
            <Alert
              message={queueT('resetStuck.error', 'Error')}
              description={resetStuckWithdrawalTransactionsMutation.error?.message || queueT('resetStuck.unknownError', 'An unknown error occurred')}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {(transactionType === 'deposit' && resetStuckDepositTransactionsMutation.isError) && (
            <Alert
              message={queueT('resetStuck.error', 'Error')}
              description={resetStuckDepositTransactionsMutation.error?.message || queueT('resetStuck.unknownError', 'An unknown error occurred')}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default QueueManagementPage;
